import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useState,
  useSyncExternalStore,
} from "react";

/**
 * What the page shows, by name, for admin Genie (GEN5-5185): the selected
 * organization and role, the open dialog, whether the admin can apply. The URL
 * only holds ids; this is what makes "this role" mean something to Genie.
 *
 * A page and each of its open dialogs publish their own part; Genie reads the
 * merged parts when a message is sent. A card that waits for a page's result
 * subscribes to one key (useGeniePageStateValue).
 */
export type GeniePageState = Record<string, string>;

type Store = {
  set: (publisher: string, state: GeniePageState) => void;
  remove: (publisher: string) => void;
  read: () => GeniePageState;
  subscribe: (listener: () => void) => () => void;
};

const createStore = (): Store => {
  // Insertion order is publishing order: the page first, then its dialogs.
  const parts = new Map<string, GeniePageState>();
  const listeners = new Set<() => void>();
  const changed = () => listeners.forEach((listener) => listener());
  return {
    set: (publisher, state) => {
      parts.set(publisher, state);
      changed();
    },
    remove: (publisher) => {
      parts.delete(publisher);
      changed();
    },
    read: () => Object.assign({}, ...parts.values()),
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
};

const NO_STORE: Store = {
  set: () => undefined,
  remove: () => undefined,
  read: () => ({}),
  subscribe: () => () => undefined,
};

const GeniePageStateContext = createContext<Store>(NO_STORE);

/** Holds what the current page publishes; GlobalLayout puts it above the page and Genie. */
export function GeniePageStateProvider({ children }: { children: ReactNode }) {
  const [store] = useState(createStore);
  return (
    <GeniePageStateContext.Provider value={store}>
      {children}
    </GeniePageStateContext.Provider>
  );
}

/**
 * Publishes part of what the page shows while the caller is mounted; null
 * publishes nothing (a closed dialog). Genie is outside React's tree of the
 * page, so the store is kept in step with an effect.
 */
export const usePublishGeniePageState = (state: GeniePageState | null) => {
  const store = useContext(GeniePageStateContext);
  const publisher = useId();
  // Compared by content: callers build a fresh object on every render.
  const serialized = state === null ? null : JSON.stringify(state);

  useEffect(() => {
    if (serialized === null) {
      return;
    }
    const parsed: unknown = JSON.parse(serialized);
    if (typeof parsed === "object" && parsed !== null) {
      store.set(
        publisher,
        Object.fromEntries(
          Object.entries(parsed).filter(
            (entry): entry is [string, string] => typeof entry[1] === "string"
          )
        )
      );
    }
    return () => store.remove(publisher);
  }, [publisher, serialized, store]);
};

/** Reads what the page shows now; called when a message is sent. */
export const useReadGeniePageState = (): (() => GeniePageState) =>
  useContext(GeniePageStateContext).read;

/** One published value, re-rendering the caller when it changes; undefined when nothing publishes it. */
export const useGeniePageStateValue = (key: string): string | undefined => {
  const store = useContext(GeniePageStateContext);
  const read = useCallback(() => store.read()[key], [key, store]);
  return useSyncExternalStore(store.subscribe, read);
};

/**
 * Everything published, as one string that changes when any part does: what
 * the shared shell's turn context re-renders on (GEN5-5269 P6), so the next
 * turn always carries what the page shows now.
 */
export const useGeniePageStateSnapshot = (): string => {
  const store = useContext(GeniePageStateContext);
  const read = useCallback(() => JSON.stringify(store.read()), [store]);
  return useSyncExternalStore(store.subscribe, read);
};
